import { useCallback, useEffect, useState } from 'react'
import { useAddNoteComment } from '@/hooks/api/dashboard'
import { useLocalStorage } from '@/hooks/common'
import { Comment } from '@/types'
import { getNowInUTC } from '@/utils/helper'
import { createAnonymousApi } from '@/utils/anonymousApi'
import toast from 'react-hot-toast'
import { fireAlertToast, getAlertCopy } from '@/design-system/alerts'

/**
 * Reading a note's comment thread is always anonymous-client — this is the
 * one call a signed-out guest makes from inside the lightbox, and the
 * authenticated `AxiosContext` client logs out + redirects to `/login` on
 * any "inactive user" response, which is exactly what an anonymous request
 * looks like. `topicNoteLoad` is exempt from that gate server-side and
 * enforces its own topic-visibility check, so this is safe for owners,
 * members, and guests alike — cookies still ride along via
 * `withCredentials`, so a real session sees the same response it always did.
 */
const anonymousApi = createAnonymousApi()

export function useAlbumComments(albumId: string | undefined, noteId: string | undefined) {
  const addComment = useAddNoteComment()
  const [ewUser] = useLocalStorage('ewUser', null)
  const [basicInfo] = useLocalStorage('ewBasicInfo', null)
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!noteId) {
      setComments([])
      return
    }
    setLoading(true)
    try {
      const response = await anonymousApi.post('/ajax/topics', {
        requestType: 'topicNoteLoad',
        format: 'json',
        noteID: noteId,
      })
      if (response.data?.isError) throw new Error('Could not load comments')
      setComments(response.data?.noteDetails?.comments || [])
    } catch {
      // Silent — an empty rail already reads fine ("Be the first to leave a
      // note"), and this fires on any note-visibility edge case (e.g. a
      // guest opening a photo whose topic isn't fully public), which isn't
      // worth alarming the viewer with a toast over.
      setComments([])
    } finally {
      setLoading(false)
    }
  }, [noteId])

  useEffect(() => {
    if (noteId) refresh()
    else setComments([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId])

  const post = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!albumId || !noteId || !trimmed) return
      if (!ewUser?.userID) {
        fireAlertToast('photo.commentSignInRequired')
        return
      }

      try {
        const data = await addComment.mutateAsync({
          topicID: albumId,
          noteID: noteId,
          comment: trimmed,
        })
        if (data?.isError) {
          throw new Error(data.errorMessage || 'Comment failed')
        }
        const next: Comment = {
          comment: data.comment ?? trimmed,
          dateAdded: getNowInUTC(),
          feedbackID: data.commentID,
          noteID: noteId,
          topicID: albumId,
          senderName: basicInfo
            ? `${basicInfo.firstName || ''} ${basicInfo.lastName || ''}`.trim()
            : 'You',
          senderID: ewUser.userID,
          userID: ewUser.userID,
          profilePicURL: basicInfo?.profilePicThumbURL || '',
        }
        setComments((prev) => [...prev, next])
        return next
      } catch (e) {
        toast.error(e instanceof Error ? e.message : getAlertCopy('photo.commentFailed').title)
        return null
      }
    },
    [albumId, noteId, ewUser, basicInfo, addComment],
  )

  return {
    comments,
    loading,
    posting: addComment.isLoading,
    /** No account = read-only. Lightbox swaps the composer for a sign-up CTA when this is false. */
    canComment: Boolean(ewUser?.userID),
    refresh,
    post,
  }
}
